import { Box, Grid, IconButton, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import React from 'react';
import { MetricRecordCaptureParameters } from '../../../views/metric_collection/MetricCollectionDataManagement';
import ReportingPeriodGroupSelect from '../../shared/input/select/ReportingPeriodGroupSelect';
import {
  Country,
  Division,
  EntityLabel,
  Region,
  ReportingPeriodGroup,
  Site,
  Subdivision
} from '@esg/esg-global-types';
import { CaptionText } from '../../shared/typography/PrimaryTypography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PrimaryTabs from '../../shared/tab/PrimaryTabs';
import PrimaryTab from '../../shared/tab/PrimaryTab';
import { formatTitleCase, values } from '../../../util/general_utils';
import PrimaryButton from '../../shared/button/PrimaryButton';
import PrimarySwitch from '../../shared/input/PrimarySwitch';
import AlertBanner from '../../shared/banner/AlertBanner';
import ColorTag from '../../shared/decoration/ColorTag';
import { ReactComponent as FilterIcon } from '../../../assets/icons/Filter.svg';
import {
  MetricRecordEntitiesMap,
  MetricRecordDataLevel,
  MetricRecordReportingEntity,
  metric_record_label
} from '../../../graphql/services/metric_record.service';
import { country_label } from '../../../lib/metric_capture/country';
import { region_label } from '../../../lib/metric_capture/region';
import { division_label } from '../../../lib/metric_capture/division';
import { subdivision_label } from '../../../lib/metric_capture/subdivision';
import { site_label } from '../../../lib/metric_capture/site';

interface MetricCollectionPersistentParametersMenuProps {
  entities_map: MetricRecordEntitiesMap;
  capture_parameters: MetricRecordCaptureParameters;
  handleParametersChange: (new_parameters: MetricRecordCaptureParameters) => void;
  handleShowMetricRecords: (show: boolean) => void;
}

interface DataLevelInfo {
  data_level: MetricRecordDataLevel;
  entity_label: EntityLabel;
  entities: Array<MetricRecordReportingEntity>;
}

/**
 * Persistent metric collection parameter setting interface for a given metric in the capturing process.
 * @param {MetricRecordEntitiesMap} entities_map Entities to display options for relative to parameter setting input.
 * @param {MetricRecordCaptureParameters} capture_parameters Current capture parameter from parent.
 * @param {(new_parameters: MetricRecordCaptureParameters): void} handleParametersChange Update capture parameter with new data in parent.
 * @param {(show: boolean): void} handleShowMetricRecords Toggle the display of metric records below the parameter.
 * @returns {JSX.Element}
 */
const MetricCollectionPersistentParametersMenu = ({
  entities_map,
  capture_parameters,
  handleParametersChange,
  handleShowMetricRecords
}: MetricCollectionPersistentParametersMenuProps) => {
  const [expanded, setExpanded] = React.useState<boolean>(true);
  const [levelTab, setLevelTab] = React.useState<number>(0);
  const theme: Theme = useTheme();

  const data_levels_info: Array<DataLevelInfo> = [
    {
      data_level: 'national',
      entity_label: country_label,
      entities: values<Country>(entities_map.countries)
    },
    {
      data_level: 'regional',
      entity_label: region_label,
      entities: values<Region>(entities_map.regions)
    },
    {
      data_level: 'divisional',
      entity_label: division_label,
      entities: values<Division>(entities_map.divisions)
    },
    {
      data_level: 'subdivisional',
      entity_label: subdivision_label,
      entities: values<Subdivision>(entities_map.subdivisions)
    },
    {
      data_level: 'site',
      entity_label: site_label,
      entities: values<Site>(entities_map.sites)
    }
  ];

  const available_data_levels: Array<DataLevelInfo> = data_levels_info.filter(
    (data_level: DataLevelInfo) => data_level.entities.length > 0
  );

  const valid_input = Boolean(
    capture_parameters.reporting_period_group && capture_parameters.data_level
  );

  const handleExpandMenu = (expand: boolean): void => {
    setExpanded(expand);
  };

  // Handler action for primary button for capturing / viewing metric records.
  const handleConfirmClick = (): void => {
    handleExpandMenu(false);
    handleShowMetricRecords(true);
  };

  // Set a selected reporting period group to the capture parameter
  const handleSelectReportingPeriodGroup = (
    reporting_period_group: ReportingPeriodGroup | null
  ): void => {
    handleParametersChange({
      ...capture_parameters,
      reporting_period_group: reporting_period_group
    });
    handleShowMetricRecords(false);
  };

  // Set the flag for averaging captured values for the reporting period group in the capture parameter.
  const handleAverageOverYearChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    handleParametersChange({
      ...capture_parameters,
      average_over_year: event.target.checked
    });
  };

  // Set the company heirarchy based data level of the capture parameter.
  const handleLevelTabChange = (new_tab: number): void => {
    setLevelTab(new_tab);
    handleParametersChange({
      ...capture_parameters,
      data_level: available_data_levels[new_tab].data_level
    });
    handleShowMetricRecords(false);
  };

  // Get the configured entities for a given reporting data level input for tooltip display.
  const getDataLevelEntities = (data_levels_index: number): string => {
    const data_level_info: DataLevelInfo = data_levels_info[data_levels_index];
    const entities: Array<MetricRecordReportingEntity> = data_level_info.entities;
    if (entities.length < 1) {
      return `No ${data_level_info.entity_label.many} configured.`;
    }
    const sliced_entity_names: string = entities
      .slice(0, 10)
      .map((entity: MetricRecordReportingEntity) => entity.name)
      .join(', ');
    return sliced_entity_names + (entities.length > 10 ? '...' : '');
  };

  // Automatically set the data level to the first one the current user has access to / entities for.
  React.useEffect(() => {
    if (available_data_levels[0]) {
      handleParametersChange({
        ...capture_parameters,
        data_level: available_data_levels[0].data_level
      });
    }
  }, [entities_map]);

  return (
    <Box>
      <Grid container spacing={6} sx={{ alignItems: 'center', px: 4, py: expanded ? 4 : 2 }}>
        {expanded ? (
          <>
            <Grid size={4}>
              <ReportingPeriodGroupSelect
                reporting_period_group_options={values<ReportingPeriodGroup>(
                  entities_map.reporting_period_groups
                )}
                selected_reporting_period_group={capture_parameters.reporting_period_group}
                handleChangeReportingPeriodGroups={(
                  reporting_period_groups: ReportingPeriodGroup | Array<ReportingPeriodGroup> | null
                ) => {
                  if (!Array.isArray(reporting_period_groups)) {
                    handleSelectReportingPeriodGroup(reporting_period_groups);
                  }
                }}
                input_label="Financial Year"
              />
            </Grid>
            {capture_parameters.metric && capture_parameters.is_quantitative && (
              <>
                <Grid size={5} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CaptionText weight="Medium">Monthly Data</CaptionText>
                  <PrimarySwitch
                    disableRipple
                    checked={capture_parameters.average_over_year === true}
                    onChange={handleAverageOverYearChange}
                  />
                  <CaptionText weight="Medium">Yearly Data</CaptionText>
                  <Tooltip
                    title={
                      <Typography fontSize="0.9rem">
                        Capturing yearly data will average the values down for each reporting month
                        within the financial year ( / 12).
                      </Typography>
                    }
                    arrow
                  >
                    <InfoOutlinedIcon color="primary" fontSize="small" />
                  </Tooltip>
                </Grid>
                {available_data_levels.length > 1 && (
                  <Grid
                    size={12}
                    sx={{ flexGrow: 1, minWidth: 0, boxSizing: 'border-box', textAlign: 'left' }}
                  >
                    <CaptionText weight="Medium" sx={{ mb: 2 }}>
                      Choose which level you want to capture for, from higher level company activity
                      ({formatTitleCase(available_data_levels[0].data_level)}) to site-level detail
                      (Site)
                    </CaptionText>
                    <PrimaryTabs
                      value={levelTab}
                      onChange={(event: React.SyntheticEvent<Element, Event>, new_tab: number) =>
                        handleLevelTabChange(new_tab)
                      }
                      sx={{ boxSizing: 'border-box' }}
                      aria-label="capture-parameter-tabs"
                      id="capture-parameter-tabs"
                    >
                      {available_data_levels.map(
                        (data_level_info: DataLevelInfo, index: number) => {
                          return (
                            <PrimaryTab
                              id={`capture-level-tab-${index}`}
                              label={formatTitleCase(data_level_info.data_level)}
                              value={index}
                              key={data_level_info.data_level}
                              sx={{ boxSizing: 'border-box' }}
                              icon={
                                <Tooltip
                                  title={
                                    <Typography fontSize="0.9rem">
                                      {getDataLevelEntities(index)}
                                    </Typography>
                                  }
                                  arrow
                                >
                                  <InfoOutlinedIcon fontSize="small" />
                                </Tooltip>
                              }
                              iconPosition="end"
                            />
                          );
                        }
                      )}
                    </PrimaryTabs>
                  </Grid>
                )}
              </>
            )}
            <Grid
              size={12}
              sx={{ display: 'flex', justifyContent: 'flex-start', boxSizing: 'border-box' }}
            >
              <PrimaryButton
                id="view-data-button"
                disabled={!valid_input}
                onClick={() => handleConfirmClick()}
              >
                {`Capture ${metric_record_label.many}`}
              </PrimaryButton>
            </Grid>
          </>
        ) : (
          <>
            <Grid
              size={'auto'}
              sx={{ display: 'flex', justifyContent: 'flex-start', boxSizing: 'border-box' }}
            >
              <IconButton
                onClick={() => setExpanded(true)}
                sx={{ color: theme.palette.primary.main }}
              >
                <FilterIcon
                  width={30}
                  fill={theme.palette.tint.main}
                  stroke={theme.palette.tint.main}
                  strokeWidth={2}
                />
              </IconButton>
            </Grid>
            <Grid size={'auto'} sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ width: '250px' }}>
                <ColorTag
                  text={capture_parameters.reporting_period_group?.name ?? ''}
                  color={theme.palette.teal.dark}
                />
              </Box>
              {capture_parameters.metric &&
                capture_parameters.is_quantitative &&
                available_data_levels.length > 1 && (
                  <Box sx={{ width: '250px' }}>
                    <ColorTag
                      text={formatTitleCase(capture_parameters.data_level ?? '')}
                      color={theme.palette.teal.dark}
                    />
                  </Box>
                )}
              {capture_parameters.metric && capture_parameters.is_quantitative && (
                <Box sx={{ width: '250px' }}>
                  <ColorTag
                    text={
                      capture_parameters.average_over_year ? 'Capture by Year' : 'Capture by Month'
                    }
                    color={theme.palette.teal.dark}
                  />
                </Box>
              )}
            </Grid>
          </>
        )}
      </Grid>
      {capture_parameters.average_over_year && (
        <Box sx={{ mb: -2 }}>
          <AlertBanner
            severity="warning"
            message={
              <Box>
                <Typography sx={{ fontSize: '0.9rem', display: 'inline-flex' }}>
                  Capturing on financial year level - new metric record values will be divided
                  evenly between reporting months.
                </Typography>
              </Box>
            }
            open
            width_perc={100}
          />
        </Box>
      )}
    </Box>
  );
};

export default MetricCollectionPersistentParametersMenu;
